import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/shared/PageLayout';
import { Button } from '@/components/ui/button';
import type { Client } from '@/lib/types';
import { ChevronLeft } from 'lucide-react';
import ClientPageContent from '@/components/clients/client-page-content';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import { useEffect, useState } from 'react';
import { subscribeToClient } from '@/components/clients/services/clients-api';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';

export default function ClientDetailPage() {
  const params = useParams<{ id: string }>();
  const clientId = params.id!;
  const [clientData, setClientData] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { setClient } = useClient();

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToClient(clientId, (data) => {
      setClientData(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [clientId, authLoading, user]);

  // Visiting a client's Client 360 profile counts as "selecting" them — this is
  // what wires the header's selected-client widget (ProductHeader) to Client 360
  // navigation, not just roster row clicks / the select-client dialog. Without
  // this, the roster row's name link (which stopPropagation()s past the row's
  // own select handler) would land here without ever touching the shared
  // client-context, leaving the header stuck on "Select Client".
  useEffect(() => {
    if (clientData) {
      setClient(clientData);
    }
  }, [clientData, setClient]);

  if (loading) {
    return (
      <PageLayout
        title="Loading..."
        isLoading={true}
        headerActions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/clients">
              <ChevronLeft className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Clients</span>
            </Link>
          </Button>
        }
      >
        <div data-testid="loading-skeleton" className="space-y-6">
          <Skeleton className="h-32 w-full rounded-xl" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!clientData) {
    return (
      <PageLayout
        title="Client not found"
        subtitle="The requested client could not be found"
        headerActions={
          <Button asChild variant="outline">
            <Link to="/clients">Back to clients</Link>
          </Button>
        }
      >
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">Client not found.</p>
          <Button asChild>
            <Link to="/clients">Back to clients</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={clientDisplayLabel(clientData)}
      subtitle="Client 360"
      compactContent
      headerActions={
        <Button variant="ghost" size="sm" asChild>
          <Link to="/clients">
            <ChevronLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Clients</span>
          </Link>
        </Button>
      }
    >
      <Breadcrumb
        showHome={false}
        className="mb-4"
        items={[
          { label: 'Clients', href: '/clients' },
          { label: clientDisplayLabel(clientData) },
        ]}
      />
      <ClientPageContent client={clientData} />
    </PageLayout>
  );
}
